// Grant <name> a product (AdminPerson.dc.html's dialog), owner and admin: the product (one they already
// have is faded; a revoked or ended one says progress is kept), the cohort for a product that has them,
// how long (kept on the grant as its length_rule, item 195), how it was paid for (Adam, 4 Oct, answer
// 2: nothing picked until Adam picks), the reason and whether to email them. grant-access reads with the
// caller's own login and writes through hub.grant_access naming the caller, so the audit names them with
// the length, and sends the access email (how to log in at the product's door) when asked.
//
// Decision 10's resit (CLAUDE.md; data-model 10.6.2): a product that releases by intake (Profit On Your
// Terms, and The Funding Engine since Adam's 10 Oct) that they already have stays pickable while
// there's an intake they aren't in. Picked, it's a resit: the intake has to be 1 they aren't in, and
// the reason is "Resit", so grant-access leaves the decision to the database, which refuses anything
// else as has_it ("<Sam> already has <product>.").
// Their first intake's row stays as it is, and the person page lists both.
import { useMemo, useState } from 'react'
import { Button, Checkbox, DateField, Dialog, Field, Options, GRANT_SOURCES, Select, TextInput, useAction, useAdmin, todayInUk, addDays } from '../ui/index.js'
import { GRANT, byBoardOrder, firstOf, isCurrent } from './words.js'
import { cohortsOf, defaultCohort, grantable } from './data.js'

export function GrantDialog({ person, access, catalogue, cohorts, initialProduct, onClose, onGranted }) {
  const { api } = useAdmin()
  const first = firstOf(person)
  const options = useMemo(() => byBoardOrder(catalogue.filter(grantable)), [catalogue])
  const current = new Set(access.filter(isCurrent).map(a => a.product_id))
  const ended = new Map(access.filter(a => !isCurrent(a)).map(a => [a.product_id, a.status]))
  // the intakes they're in now, by product, and the products they could resit
  const heldIntakes = id => new Set(access.filter(a => isCurrent(a) && a.product_id === id && a.cohort_id).map(a => a.cohort_id))
  const resitIntakes = p => (p?.release_basis === 'own_intake' ? cohortsOf(cohorts, p.id).filter(c => !heldIntakes(p.id).has(c.id)) : [])
  const resitOk = p => current.has(p.id) && resitIntakes(p).length > 0
  const startProduct = initialProduct && !current.has(initialProduct) ? initialProduct : null
  const [productId, setProductId] = useState(startProduct)
  const product = options.find(p => p.id === productId) || null
  const resit = Boolean(product && current.has(product.id))
  const [cohortId, setCohortId] = useState(() => defaultCohort(product, cohorts, todayInUk()))
  const [length, setLength] = useState('same_as_buyer')
  const [until, setUntil] = useState('')
  const [source, setSource] = useState(null)
  const [reason, setReason] = useState('')
  const [email, setEmail] = useState(true)
  const grant = useAction(body => api.grant_access(body))

  const productCohorts = !product ? [] : resit ? resitIntakes(product) : cohortsOf(cohorts, product.id)
  const needDate = length === 'until_date' && !until
  const needIntake = resit && !cohortId
  const ready = product && reason.trim() && source && !needDate && !needIntake
  const label = !product ? GRANT.pick : needIntake ? GRANT.needIntake : !reason.trim() ? GRANT.needReason : !source ? GRANT.needSource : needDate ? GRANT.needDate : GRANT.go

  function pick(id) {
    const p = options.find(x => x.id === id)
    if (current.has(id) && !resitOk(p)) return
    const wasResit = resit
    setProductId(id)
    if (current.has(id)) {
      // a resit: the next intake they aren't in, and the reason the database asks for
      const open = resitIntakes(p)
      setCohortId(open.find(c => c.id === defaultCohort(p, open, todayInUk()))?.id || open[0]?.id || '')
      setReason(GRANT.resitReason)
    } else {
      setCohortId(defaultCohort(p, cohorts, todayInUk()))
      if (wasResit) setReason('')
    }
    grant.clear()
  }

  async function submit() {
    if (!ready) return
    const body = { person_id: person.id, product_id: product.id, source, reason: reason.trim(), email_them: email }
    if (cohortId) body.cohort_id = cohortId
    if (length === 'until_date') body.ends_on = until
    // the length picked is kept on the grant, as a bulk grant's is (item 195); both other lengths leave
    // the end open
    body.length_rule = length
    const answer = await grant.run(body)
    if (answer) onGranted?.(answer)
  }

  return (
    <Dialog title={GRANT.title(first)} onClose={onClose} busy={grant.busy}
      footer={<Button kind="ink" className="apl-grant-go" onClick={submit} disabled={!ready} busy={grant.busy} busyLabel={GRANT.busy}>{label}</Button>}>
      <div className="ad-field">
        <span className="ad-label" id="apl-grant-product">{GRANT.product}</span>
        <Options labelledBy="apl-grant-product" value={productId} onChange={pick}
          options={options.map(p => ({
            value: p.id, label: p.name, product: p, disabled: current.has(p.id) && !resitOk(p),
            note: current.has(p.id) ? (resitOk(p) ? GRANT.hasItResit : GRANT.hasIt) : ended.get(p.id) === 'revoked' ? GRANT.revoked : ended.has(p.id) ? GRANT.ended : undefined,
          }))} />
      </div>
      {productCohorts.length > 0 && (
        <Field label={resit ? GRANT.resitCohort : GRANT.cohort} hint={resit ? GRANT.resitHelp(first, cohortsOf(cohorts, product.id).filter(c => heldIntakes(product.id).has(c.id)).map(c => c.name)) : undefined}>
          {props => <Select {...props} value={cohortId} onChange={setCohortId} placeholder={resit ? undefined : GRANT.noCohort} options={productCohorts.map(c => ({ value: c.id, label: c.name }))} />}
        </Field>
      )}
      <div className="ad-field">
        <span className="ad-label" id="apl-grant-length">{GRANT.length}</span>
        <Options labelledBy="apl-grant-length" row value={length} onChange={setLength} options={GRANT.lengths} />
        {length === 'until_date' && <DateField label={GRANT.until} value={until} onChange={setUntil} min={addDays(todayInUk(), 1)} plain />}
      </div>
      <div className="ad-field">
        <span className="ad-label" id="apl-grant-source">{GRANT.source}</span>
        <Options labelledBy="apl-grant-source" row value={source} onChange={setSource} options={GRANT_SOURCES} />
      </div>
      <Field label={GRANT.reason} hint={resit ? GRANT.resitReasonHelp : GRANT.reasonHelp}>
        {props => <TextInput {...props} value={reason} onChange={setReason} placeholder={GRANT.reasonPlaceholder} maxLength={1000} readOnly={resit} />}
      </Field>
      <Checkbox checked={email} onChange={setEmail}>{GRANT.email(first)}</Checkbox>
      {grant.error && <p className="ad-error" role="alert">{grant.error.message}</p>}
    </Dialog>
  )
}
